import React, { useMemo, useState } from "react";
import axios from "axios";
import { RefreshCw, Star, Search } from "lucide-react";
import { toast } from "sonner";
import { API, allProducts, loadCatalog, fmtPrice } from "../../lib/store";
import { fmtDate } from "./adminShared";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export function ProductsTab({ auth, syncInfo, setSyncInfo, featured, setFeatured }) {
  const [syncing, setSyncing] = useState(false);
  const [productSearch, setProductSearch] = useState("");

  const gridProducts = useMemo(() => {
    if (!productSearch) {
      return allProducts.filter((p, idx) => featured.includes(p.handle) || idx < 24).slice(0, 60);
    }
    const q = productSearch.toLowerCase();
    return allProducts
      .filter((p) => p.title.toLowerCase().includes(q) || p.tags.some((t) => t.toLowerCase().includes(q)))
      .slice(0, 60);
  }, [productSearch, featured]);

  const runSync = async () => {
    setSyncing(true);
    toast.info("Syncing products from Shopify — this takes 15-30 seconds...");
    try {
      const res = await axios.post(`${API}/admin/sync-products`, {}, auth);
      toast.success(`Sync complete! ${res.data.productCount} products updated.`);
      setSyncInfo((s) => ({ ...s, lastSync: res.data.syncedAt, productCount: res.data.productCount }));
      await loadCatalog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed — please try again");
    } finally {
      setSyncing(false);
    }
  };

  const toggleFeatured = (handle) => {
    setFeatured((f) => {
      if (f.includes(handle)) return f.filter((h) => h !== handle);
      if (f.length >= 8) { toast.error("Maximum 8 featured pieces"); return f; }
      return [...f, handle];
    });
  };

  const saveFeatured = async () => {
    try {
      await axios.post(`${API}/admin/featured`, { handles: featured }, auth);
      await loadCatalog();
      toast.success("Featured products saved — home page updated!");
    } catch { toast.error("Could not save featured products"); }
  };

  return (
    <div className="space-y-8">
      {/* Sync card */}
      <div className="bg-white border border-[#eee6d6] p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="font-heading text-xl text-[#171310]">Shopify Product Sync</h3>
          <p className="text-sm text-[#8f8571] mt-1">
            Pulls the latest products & prices from your Shopify store — including the live gold price updates made by Live Gold Price Editor. Also runs automatically every 30 minutes.
          </p>
          {syncInfo?.lastSync && (
            <p className="text-xs text-[#a8842c] mt-2">
              Last synced: {fmtDate(syncInfo.lastSync)} · {syncInfo.productCount} products
            </p>
          )}
        </div>
        <Button onClick={runSync} disabled={syncing} className="rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.12em] uppercase text-xs h-11 px-6 transition-colors duration-300" data-testid="admin-sync-btn">
          <RefreshCw size={15} className={`mr-2 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing..." : "Sync Products Now"}
        </Button>
      </div>

      {/* Featured picker */}
      <div className="bg-white border border-[#eee6d6] p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <div>
            <h3 className="font-heading text-xl text-[#171310] flex items-center gap-2">
              <Star size={18} className="text-[#a8842c]" /> Featured Products ({featured.length}/8)
            </h3>
            <p className="text-sm text-[#8f8571] mt-1">Click pieces to feature them on the home page. Save when done.</p>
          </div>
          <Button onClick={saveFeatured} className="rounded-none bg-[#a8842c] hover:bg-[#c09a3e] text-[#171310] font-semibold tracking-[0.12em] uppercase text-xs h-10 px-6 transition-colors duration-300" data-testid="admin-save-featured-btn">
            Save Featured
          </Button>
        </div>

        {/* Selected chips */}
        {featured.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-5">
            {featured.map((h) => {
              const p = allProducts.find((x) => x.handle === h);
              return (
                <button key={h} onClick={() => toggleFeatured(h)} className="flex items-center gap-1.5 bg-[#171310] text-[#e9d9ac] text-xs px-3 py-1.5 hover:bg-[#8c2f1b] transition-colors duration-200" title="Click to remove">
                  <Star size={11} className="fill-[#e9d9ac]" /> {(p?.title || h).slice(0, 38)}
                </button>
              );
            })}
          </div>
        )}

        {/* Search */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9c9482]" />
          <Input value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Search your 1,300+ products by name or tag..." className="rounded-none border-[#d8cdb5] pl-9 h-11" data-testid="admin-product-search-input" />
        </div>

        {/* Results grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[520px] overflow-y-auto pr-1">
          {gridProducts.map((p) => {
            const isSel = featured.includes(p.handle);
            return (
              <button
                key={p.id}
                onClick={() => toggleFeatured(p.handle)}
                className={`text-left border p-2 transition-colors duration-200 ${isSel ? "border-[#a8842c] bg-[#faf5ea]" : "border-[#eee6d6] bg-white hover:border-[#cdb87a]"}`}
              >
                <div className="aspect-square bg-[#faf7f0] overflow-hidden relative">
                  <img src={p.images[0]} alt={p.title} loading="lazy" className="w-full h-full object-contain p-1" />
                  {isSel && <Star size={16} className="absolute top-1 right-1 fill-[#a8842c] text-[#a8842c]" />}
                </div>
                <p className="text-[11px] text-[#3a352c] mt-1.5 line-clamp-2 leading-snug">{p.title}</p>
                <p className="text-xs font-medium text-[#171310] mt-0.5">{fmtPrice(p.price)}</p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
